import {
  FileImage,
  Image as ImageIcon,
  ImagePlus,
  Loader2,
  Sparkles,
  Upload,
  X,
} from "lucide-react";
import React, { ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "../../contexts/LanguageContext";
import { showToastError } from "../../shared/alertService";
import {
  IMAGE_PROVIDER_OPTIONS,
  imageProviderHint,
} from "../../shared/imageProviders";
import { cn } from "../../utils/cn";
import Button from "../ui/Button";
import Input from "../ui/Input";
import Modal from "../ui/Modal";
import Select from "../ui/Select";
import Textarea from "../ui/Textarea";

/** Limitações de upload (alinhadas ao backend: upload_image.py) */
const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
];
const MAX_IMAGE_SIZE_MB = 10;
const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024;
const ACCEPT_UPLOAD = ".jpg,.jpeg,.png,.gif,.webp";

interface ImageUploadModalProps {
  open: boolean;
  onClose: () => void;
  onUpload: () => void;
  onGenerate: () => void;
  loadingUpload: boolean;
  loadingGenerate: boolean;
  postId: number | null;
  selectedFile: File | null;
  setSelectedFile: (file: File | null) => void;
  customPrompt: string;
  setCustomPrompt: (prompt: string) => void;
  imageRestrictions: string;
  setImageRestrictions: (restrictions: string) => void;
  imageProvider: string;
  setImageProvider: (provider: string) => void;
  logoFile: File | null;
  setLogoFile: (file: File | null) => void;
  logoPreview: string | null;
  setLogoPreview: (preview: string | null) => void;
  referenceImages: File[];
  setReferenceImages: (images: File[]) => void;
  referenceImagePreviews: string[];
  setReferenceImagePreviews: (previews: string[]) => void;
  /** Se o post tem conteúdo (texto). Backend usa como prompt quando não há prompt/arquivos. */
  postHasContent?: boolean;
  mode?: "upload" | "generate";
  imageJobStatus?: "idle" | "pending" | "processing" | "completed" | "failed";
}

const ImageUploadModal: React.FC<ImageUploadModalProps> = ({
  open,
  onClose,
  onUpload,
  onGenerate,
  loadingUpload,
  loadingGenerate,
  postId,
  selectedFile,
  setSelectedFile,
  customPrompt,
  setCustomPrompt,
  imageRestrictions,
  setImageRestrictions,
  imageProvider,
  setImageProvider,
  logoFile,
  setLogoFile,
  logoPreview,
  setLogoPreview,
  referenceImages,
  setReferenceImages,
  referenceImagePreviews,
  setReferenceImagePreviews,
  postHasContent = false,
  mode = "upload",
  imageJobStatus = "idle",
}) => {
  const { t } = useTranslation();
  const supportsLogoAndReferences =
    imageProvider === "heygen" || imageProvider === "glm-image";

  useEffect(() => {
    if (!supportsLogoAndReferences) {
      setLogoFile(null);
      setLogoPreview(null);
      setReferenceImages([]);
      setReferenceImagePreviews([]);
    }
  }, [
    supportsLogoAndReferences,
    setLogoFile,
    setLogoPreview,
    setReferenceImages,
    setReferenceImagePreviews,
  ]);

  const handlePromptChange = (e: ChangeEvent<HTMLInputElement>) => {
    setCustomPrompt(e.target.value);
  };

  const handleLogoChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setLogoFile(file);

      // Criar preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
  };

  const handleReferenceImagesChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      const newImages = [...referenceImages, ...files];
      setReferenceImages(newImages);

      // Criar previews
      const newPreviews: string[] = [];
      files.forEach((file) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          newPreviews.push(reader.result as string);
          if (newPreviews.length === files.length) {
            setReferenceImagePreviews([
              ...referenceImagePreviews,
              ...newPreviews,
            ]);
          }
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const handleRemoveReferenceImage = (index: number) => {
    const newImages = referenceImages.filter((_, i) => i !== index);
    const newPreviews = referenceImagePreviews.filter((_, i) => i !== index);
    setReferenceImages(newImages);
    setReferenceImagePreviews(newPreviews);
  };

  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const processFile = (file: File) => {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      showToastError(
        t(
          "Formato não suportado ({name}). Use: JPG, PNG, GIF ou WebP. Tamanho máx.: {max}MB.",
          { name: file.name, max: MAX_IMAGE_SIZE_MB },
        ),
      );
      return;
    }
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
      showToastError(
        t("Arquivo muito grande ({size}MB). Tamanho máximo: {max}MB.", {
          size: sizeMB,
          max: MAX_IMAGE_SIZE_MB,
        }),
      );
      return;
    }
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleFileChangeWithPreview = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      processFile(e.target.files[0]);
    } else {
      setSelectedFile(null);
      setImagePreview(null);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const modalTitle =
    mode === "upload"
      ? t("Upload de Imagem - Post #{postId}", { postId: postId || "" })
      : t("Gerar Imagem com IA - Post #{postId}", { postId: postId || "" });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={modalTitle}
      maxWidth="max-w-3xl"
      footer={
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loadingUpload || loadingGenerate}
            size="sm"
          >
            {t("Cancelar")}
          </Button>
          {mode === "upload" && (
            <Button
              type="button"
              variant="primary"
              icon={
                loadingUpload ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4" />
                )
              }
              onClick={onUpload}
              disabled={loadingUpload || !selectedFile}
              size="sm"
            >
              {loadingUpload ? t("Enviando...") : t("Enviar Imagem")}
            </Button>
          )}
          {mode === "generate" && (
            <Button
              type="button"
              variant="primary"
              icon={
                loadingGenerate ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )
              }
              onClick={onGenerate}
              disabled={
                loadingGenerate ||
                (!customPrompt.trim() &&
                  !(
                    supportsLogoAndReferences &&
                    (logoFile || referenceImages.length > 0)
                  ) &&
                  !postHasContent)
              }
              size="sm"
            >
              {loadingGenerate
                ? imageJobStatus === "processing"
                  ? t("Gerando imagem...")
                  : imageJobStatus === "pending"
                    ? t("Na fila...")
                    : t("Gerando Imagem...")
                : t("Gerar Imagem com IA")}
            </Button>
          )}
        </div>
      }
    >
      <div className="space-y-4">
        {/* Seção: Upload de Imagem - Mostrar apenas se mode === 'upload' */}
        {mode === "upload" && (
          <div className="space-y-3">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-1.5">
                {t("Selecione uma imagem")}
              </label>
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={cn(
                  "border-2 border-dashed rounded-lg p-6 text-center transition-all cursor-pointer bg-white",
                  isDragging
                    ? "border-blue-500 bg-blue-50 scale-[1.02]"
                    : "border-blue-300 hover:border-blue-400",
                )}
              >
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept={ACCEPT_UPLOAD}
                    onChange={handleFileChangeWithPreview}
                    className="hidden"
                    aria-label={t("Upload de imagem")}
                    disabled={loadingUpload || loadingGenerate}
                  />
                  <div className="flex flex-col items-center gap-2">
                    <ImageIcon
                      className={cn(
                        "h-8 w-8 transition-colors",
                        isDragging ? "text-blue-600" : "text-blue-500",
                      )}
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-700">
                        {t("Clique para selecionar")}
                      </span>
                      <span className="text-xs text-gray-500 block mt-1">
                        {t("ou arraste e solte uma imagem aqui")}
                      </span>
                    </div>
                    <span
                      className="text-[10px] text-gray-500"
                      title={t("Limitações do servidor")}
                    >
                      {t("JPG, PNG, GIF, WebP. Tamanho máx.: {max}MB", {
                        max: MAX_IMAGE_SIZE_MB,
                      })}
                    </span>
                  </div>
                </label>
              </div>
            </div>

            {selectedFile && (
              <div className="mt-3 p-3 rounded-lg border border-purple-200 bg-gray-50/50">
                <div className="flex items-start gap-3">
                  {imagePreview && (
                    <div className="shrink-0">
                      <img
                        src={imagePreview}
                        alt={t("Preview")}
                        className="w-20 h-20 object-cover rounded-lg border border-purple-200"
                      />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <FileImage className="h-4 w-4 text-blue-600 shrink-0" />
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {selectedFile.name}
                      </p>
                    </div>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(selectedFile.size)} • {selectedFile.type}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedFile(null);
                      setImagePreview(null);
                    }}
                    className="shrink-0 p-1 text-gray-400 hover:text-red-600 transition-colors"
                    disabled={loadingUpload || loadingGenerate}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Seção: Gerar Imagem com IA - Mostrar apenas se mode === 'generate' */}
        {mode === "generate" && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Select
                label={t("Provedor de IA")}
                value={imageProvider}
                onChange={(e) => setImageProvider(e)}
                options={[...IMAGE_PROVIDER_OPTIONS]}
                size="md"
                disabled={loadingUpload || loadingGenerate}
              />
              <p className="text-xs text-gray-500">
                {imageProviderHint(imageProvider) &&
                  `✨ ${t(imageProviderHint(imageProvider))}`}
              </p>
            </div>
            <Input
              label={t("Gerar Imagem com IA (Prompt)")}
              type="text"
              value={customPrompt}
              onChange={handlePromptChange}
              placeholder={t("Descreva a imagem desejada...")}
              inputSize="md"
              disabled={loadingUpload || loadingGenerate}
            />
            <Textarea
              label={t("Restrições e Instruções (Opcional)")}
              value={imageRestrictions}
              onChange={(e) => setImageRestrictions(e.target.value)}
              placeholder={t("Ex: não incluir texto, não incluir logo, etc.")}
              inputSize="md"
              disabled={loadingUpload || loadingGenerate}
              hint={t(
                "Especifique restrições ou instruções específicas para a geração da imagem. Estas serão incluídas no contexto de geração. Deixe vazio para usar o contexto do post.",
              )}
            />

            {supportsLogoAndReferences ? (
              <>
                <div className="space-y-2">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-1.5">
                    {t("Logo da Empresa")}{" "}
                    <span className="text-gray-400 font-normal">
                      ({t("Opcional")})
                    </span>
                  </label>
                  {!logoFile ? (
                    <div className="border-2 border-dashed border-purple-300 rounded-lg p-4 text-center hover:border-purple-400 transition-colors cursor-pointer">
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleLogoChange}
                          className="hidden"
                          disabled={loadingUpload || loadingGenerate}
                          aria-label={t("Upload de logo")}
                        />
                        <div className="flex flex-col items-center gap-2">
                          <ImagePlus className="h-6 w-6 text-gray-400" />
                          <span className="text-xs text-gray-600">
                            {t("Clique para adicionar logo")}
                          </span>
                          <span className="text-[10px] text-gray-400">
                            {t("PNG, JPG até 5MB")}
                          </span>
                        </div>
                      </label>
                    </div>
                  ) : (
                    <div className="relative inline-block">
                      <div className="relative w-32 h-32 border-2 border-purple-300 rounded-lg overflow-hidden bg-white shadow-xs">
                        <img
                          src={logoPreview || ""}
                          alt={t("Logo preview")}
                          className="w-full h-full object-contain p-2"
                        />
                        <button
                          type="button"
                          onClick={handleRemoveLogo}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors shadow-md"
                          disabled={loadingUpload || loadingGenerate}
                          aria-label={t("Remover logo")}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                      <p className="mt-1 text-xs text-gray-600 truncate max-w-[128px]">
                        {logoFile.name}
                      </p>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-1.5">
                    {t("Imagens de Referência")}{" "}
                    <span className="text-gray-400 font-normal">
                      ({t("Opcional")})
                    </span>
                  </label>
                  <div className="border-2 border-dashed border-purple-300 rounded-lg p-3 text-center hover:border-purple-400 transition-colors cursor-pointer">
                    <label className="cursor-pointer block">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleReferenceImagesChange}
                        className="hidden"
                        disabled={loadingUpload || loadingGenerate}
                        aria-label={t("Upload de imagens de referência")}
                      />
                      <div className="flex flex-col items-center gap-2">
                        <ImagePlus className="h-6 w-6 text-gray-400" />
                        <span className="text-xs text-gray-600">
                          {t("Clique para adicionar imagens de referência")}
                        </span>
                        <span className="text-[10px] text-gray-400">
                          {t("Múltiplas imagens permitidas")}
                        </span>
                      </div>
                    </label>
                  </div>
                  {referenceImagePreviews.length > 0 && (
                    <div className="grid grid-cols-4 gap-2 mt-3">
                      {referenceImagePreviews.map((preview, index) => (
                        <div key={index} className="relative group">
                          <div className="relative w-full aspect-square border-2 border-purple-200 rounded-lg overflow-hidden bg-white shadow-xs">
                            <img
                              src={preview}
                              alt={t("Referência {index}", {
                                index: index + 1,
                              })}
                              className="w-full h-full object-cover"
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveReferenceImage(index)}
                              className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors opacity-0 group-hover:opacity-100 shadow-md"
                              disabled={loadingUpload || loadingGenerate}
                              aria-label={t("Remover imagem {index}", {
                                index: index + 1,
                              })}
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                          <p className="mt-1 text-[10px] text-gray-600 truncate">
                            {referenceImages[index]?.name}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <p className="text-xs text-gray-500 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                {t("Logo e imagens de referência estão disponíveis apenas com")}{" "}
                <strong>HeyGen</strong> {t("ou")} <strong>GLM-Image</strong>.
              </p>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ImageUploadModal;
